import { Check, MessageSquare, ThumbsUp } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import { useParams } from 'react-router-dom';
import { AudienceWelcome } from '../components/AudienceWelcome';
import { FlowerButton } from '../components/FlowerButton';
import { StatusPanel } from '../components/StatusPanel';
import { displayNickname, useEvent } from '../context/EventContext';
import { api } from '../lib/api';
import { validateComment } from '../lib/validation';
import type { Comment, MyEngagement } from '../types';

function timeLabel(value: string): string {
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function TopicView() {
  const { topicId } = useParams();
  const {
    snapshot,
    loading,
    error,
    online,
    nickname,
    setNickname,
    refresh,
    updateTopic,
    queueFlower,
    pendingFlowersFor,
    flowerError,
    setHasCommentDraft,
  } = useEvent();
  const topic = snapshot?.topics.find((item) => item.id === topicId);
  const topicIsAvailable =
    Boolean(snapshot?.settings.audienceBrowsingEnabled) ||
    snapshot?.settings.currentTopicId === topicId;
  const [comments, setComments] = useState<Comment[]>([]);
  const [detailLoading, setDetailLoading] = useState(true);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [engagement, setEngagement] = useState<MyEngagement | null>(null);
  const [engagementReady, setEngagementReady] = useState(false);
  const [likeSaving, setLikeSaving] = useState(false);
  const [interactionError, setInteractionError] = useState<string | null>(null);

  useEffect(() => {
    if (!topicId || !online || !topicIsAvailable) {
      setEngagementReady(false);
      return;
    }
    let cancelled = false;
    const loadEngagement = async () => {
      try {
        const [result] = await api.getMyEngagements([topicId]);
        if (cancelled) return;
        setEngagement(result ?? { topicId, liked: false, flowerCount: 0 });
        setEngagementReady(true);
        setInteractionError(null);
      } catch {
        if (!cancelled) {
          setInteractionError('Like state could not be loaded. Refresh to retry.');
        }
      }
    };
    void loadEngagement();
    return () => {
      cancelled = true;
    };
  }, [online, topicId, topicIsAvailable]);

  useEffect(() => {
    setHasCommentDraft(Boolean(commentBody.trim()));
    return () => setHasCommentDraft(false);
  }, [commentBody, setHasCommentDraft]);

  const loadDetail = useCallback(async () => {
    if (!topicId || !online || !topicIsAvailable) return;
    try {
      const nextComments = await api.getComments(topicId);
      setComments(nextComments);
      setLocalError(null);
    } catch {
      setLocalError('Topic activity could not be refreshed.');
    } finally {
      setDetailLoading(false);
    }
  }, [online, topicId, topicIsAvailable]);

  useEffect(() => {
    void loadDetail();
    let timer = 0;
    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === 'visible') await loadDetail();
        schedule();
      }, 8_000 + Math.floor(Math.random() * 4_001));
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [loadDetail]);

  const commentsDisabled =
    !online ||
    !snapshot?.settings.eventEnabled ||
    detailLoading ||
    !snapshot?.settings.commentsEnabled;

  const reactionsDisabled = !online || !snapshot?.settings.eventEnabled;

  const toggleLike = async () => {
    if (!topic || !engagement || !engagementReady || likeSaving || reactionsDisabled) return;
    const previousLiked = engagement.liked;
    const previousCount = topic.likeCount;
    const target = !previousLiked;
    setLikeSaving(true);
    setEngagement({ ...engagement, liked: target });
    updateTopic(topic.id, {
      likeCount: Math.max(0, previousCount + (target ? 1 : -1)),
    });
    try {
      const result = await api.setLike(topic.id, target, snapshot?.settings.interactionGeneration);
      setEngagement((current) =>
        current ? { ...current, liked: result.liked } : current,
      );
      updateTopic(topic.id, { likeCount: result.likeCount });
      setInteractionError(null);
    } catch {
      setEngagement((current) =>
        current ? { ...current, liked: previousLiked } : current,
      );
      updateTopic(topic.id, { likeCount: previousCount });
      setInteractionError('Like was not saved. Check your connection and try again.');
    } finally {
      setLikeSaving(false);
    }
  };

  const postComment = async (event: FormEvent) => {
    event.preventDefault();
    if (!topic || commentSaving || commentsDisabled) return;
    const validationError = validateComment(commentBody);
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    if (!online) {
      setLocalError('Comments cannot be posted offline.');
      return;
    }
    setCommentSaving(true);
    try {
      const result = await api.createComment(
        topic.id,
        displayNickname(nickname),
        commentBody.trim(),
        snapshot?.settings.interactionGeneration,
      );
      setComments((current) => [result.comment, ...current]);
      updateTopic(topic.id, { commentCount: result.commentCount });
      setCommentBody('');
      setLocalError(null);
    } catch {
      setLocalError('Comment was not posted. Wait a moment and try again.');
    } finally {
      setCommentSaving(false);
    }
  };

  const statusMessage = useMemo(() => {
    if (!online) return 'Comments are unavailable while offline.';
    if (localError) return localError;
    if (!snapshot?.settings.eventEnabled) return 'Audience interaction is paused.';
    return null;
  }, [
    localError,
    online,
    snapshot?.settings.eventEnabled,
  ]);

  if ((loading || detailLoading) && !topic) {
    return <div className="detail-skeleton" aria-label="Loading topic" />;
  }
  if (!topic || !snapshot) {
    return (
      <StatusPanel
        title="Topic not found"
        message={error ?? 'This presentation may not be published.'}
        action={() => void refresh()}
      />
    );
  }
  if (!topicIsAvailable) {
    return <AudienceWelcome currentTopicId={snapshot.settings.currentTopicId} />;
  }

  return (
    <div className="page detail-page">
      <header
        className={`detail-header ${
          snapshot.settings.currentTopicId === topic.id ? 'is-current' : ''
        }`}
      >
        {snapshot.settings.currentTopicId === topic.id && (
          <span className="current-topic-label">Now presenting</span>
        )}
        <p>{topic.presenter}</p>
        <h1>{topic.title}</h1>
        <p className="detail-description">{topic.description}</p>
      </header>

      {statusMessage && (
        <div className="inline-status" role="status" aria-live="polite">
          {statusMessage}
        </div>
      )}

      {(interactionError || flowerError) && (
        <div className="inline-status" role="status" aria-live="polite">
          {interactionError ?? flowerError}
        </div>
      )}

      <section className="detail-reactions" aria-labelledby="reactions-title">
        <div className="section-heading">
          <div>
            <h2 id="reactions-title">Show your support</h2>
            <p>React to this presentation while you are here.</p>
          </div>
        </div>
        <div className="detail-reaction-grid">
          <button
            className={`reaction-button like ${engagement?.liked ? 'is-active' : ''}`}
            type="button"
            disabled={reactionsDisabled || !engagementReady || likeSaving}
            onClick={() => void toggleLike()}
            aria-pressed={engagement?.liked ?? false}
            aria-label={`${engagement?.liked ? 'Unlike' : 'Like'} ${topic.title}. ${topic.likeCount} likes total`}
          >
            {engagement?.liked ? <Check /> : <ThumbsUp />}
            <span>{engagement?.liked ? 'Liked' : 'Like'}</span>
            <strong>{topic.likeCount.toLocaleString()}</strong>
          </button>
          <FlowerButton
            disabled={reactionsDisabled}
            total={topic.flowerCount + pendingFlowersFor(topic.id)}
            onFlower={() => queueFlower(topic.id)}
          />
        </div>
      </section>

      {topic.discussionQuestion && (
        <section className="topic-discussion" aria-labelledby="discussion-title">
          <h2 id="discussion-title">Discussion / Questions</h2>
          {topic.discussionQuestion.split('\n').map((question) => (
            <p className="topic-discussion-question" key={question}>{question}</p>
          ))}
          {topic.discussionOptions.length > 0 && (
            <ol className="topic-discussion-options" type="A">
              {topic.discussionOptions.map((option) => (
                <li key={option}>{option}</li>
              ))}
            </ol>
          )}
        </section>
      )}

      <section className="comments-section" aria-labelledby="comments-title">
        <div className="section-heading">
          <div>
            <h2 id="comments-title">Conversation</h2>
            <p>
              {topic.commentCount.toLocaleString()} public comment
              {topic.commentCount === 1 ? '' : 's'}
            </p>
          </div>
          <MessageSquare aria-hidden="true" />
        </div>

        <form className="comment-form" onSubmit={(event) => void postComment(event)}>
          <label htmlFor="nickname">Display name <span>optional</span></label>
          <input
            id="nickname"
            value={nickname}
            maxLength={40}
            placeholder="Anonymous"
            autoComplete="nickname"
            onChange={(event) => setNickname(event.target.value)}
            disabled={commentSaving}
          />
          <label htmlFor="comment">Your comment</label>
          <textarea
            id="comment"
            value={commentBody}
            maxLength={500}
            rows={4}
            placeholder={
              commentsDisabled
                ? 'Comments are not available right now.'
                : 'Add to the conversation…'
            }
            onChange={(event) => setCommentBody(event.target.value)}
            disabled={commentsDisabled || commentSaving}
          />
          <div className="comment-form-footer">
            <span>{commentBody.length}/500</span>
            <button
              className="button primary"
              type="submit"
              disabled={
                commentsDisabled ||
                commentSaving ||
                Boolean(validateComment(commentBody))
              }
            >
              {commentSaving ? 'Posting…' : 'Post comment'}
            </button>
          </div>
        </form>

        <div className="comment-list" aria-live="polite">
          {comments.length ? (
            comments.map((comment) => (
              <article className="comment" key={comment.id}>
                <div>
                  <strong>{comment.nickname}</strong>
                  <time dateTime={comment.createdAt}>
                    {timeLabel(comment.createdAt)}
                  </time>
                </div>
                <p>{comment.body}</p>
              </article>
            ))
          ) : (
            <div className="empty-state">
              <h3>Start the conversation.</h3>
              <p>Comments appear here after they are posted.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
